import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Modal, Button, Form, Tab, Tabs, Alert } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaEye } from "react-icons/fa";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import axios from "axios";
import GLOBALS from "../../utils/Globals";
import Footer from "../Footer/Footer";
import { toast } from "react-toastify";

const AIFieldLogicManager = () => {
  const [rowData, setRowData] = useState([]); // Initialize as empty array
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [addFieldSection, setAddFieldSection] = useState('');
  const [newFieldName, setNewFieldName] = useState('');
  const [formData, setFormData] = useState({
    customer: '',
    fields: {
      Products: {},
      Marketing: {},
      Ingredients: {},
      "Allergens and Diet": {},
      Nutrition: {},
      "External Info": {},
      Hierarchy: {},
      Contacts: {}
    }
  });

  // Field sections configuration
  const fieldSections = [
    'Products',
    'Marketing', 
    'Ingredients',
    'Allergens and Diet',
    'Nutrition',
    'External Info',
    'Hierarchy',
    'Contacts'
  ];

  // API URLs
  const baseURLGetAIFieldLogic = process.env.REACT_APP_ENV === "PROD"
    ? GLOBALS.PROD.baseURLGetAIFieldLogic
    : GLOBALS.DEV.baseURLGetAIFieldLogic;

  const baseURLCreateAIFieldLogic = process.env.REACT_APP_ENV === "PROD"
    ? GLOBALS.PROD.baseURLCreateAIFieldLogic
    : GLOBALS.DEV.baseURLCreateAIFieldLogic;

  const baseURLUpdateAIFieldLogic = process.env.REACT_APP_ENV === "PROD"
    ? GLOBALS.PROD.baseURLUpdateAIFieldLogic
    : GLOBALS.DEV.baseURLUpdateAIFieldLogic;

  const baseURLDeleteAIFieldLogic = process.env.REACT_APP_ENV === "PROD"
    ? GLOBALS.PROD.baseURLDeleteAIFieldLogic
    : GLOBALS.DEV.baseURLDeleteAIFieldLogic;

  // Column definitions for AG Grid
  const columnDefs = [
    {
      headerName: "Customer",
      field: "customer",
      sortable: true,
      filter: true,
      flex: 1,
      cellRenderer: (params) => {
        const customerName = params.value;
        const isHaribo = customerName && customerName.toLowerCase().includes('haribo');

        return (
          <div className="d-flex align-items-center">
            {isHaribo && (
              <span
                className="me-2"
                style={{
                  fontSize: '16px',
                  filter: 'hue-rotate(45deg) saturate(1.2)'
                }}
                title="Haribo Customer"
              >
                🍬
              </span>
            )}
            <span>{customerName}</span>
          </div>
        );
      }
    },
    {
      headerName: "Created Date",
      field: "created_date",
      sortable: true,
      filter: "agDateColumnFilter",
      flex: 1,
      valueFormatter: (params) => {
        if (params.value) {
          return new Date(params.value).toLocaleDateString();
        }
        return "";
      }
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: (params) => (
        <div className="d-flex gap-2 align-items-center justify-content-center h-100">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => handleView(params.data)}
          >
            <FaEye />
          </Button>
          <Button
            size="sm"
            variant="outline-warning"
            onClick={() => handleEdit(params.data)}
          >
            <FaEdit />
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => handleDelete(params.data)}
          >
            <FaTrash />
          </Button>
        </div>
      ),
      flex: 1,
      sortable: false,
      filter: false,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px'
      }
    }
  ];

  // Fetch data on component mount
  useEffect(() => {
    fetchAIFieldLogicData();
  }, []);

  const fetchAIFieldLogicData = async () => {
    try {
      setLoading(true);
      console.log('=== FETCHING AI FIELD LOGIC DATA ===');
      console.log('URL:', baseURLGetAIFieldLogic);
      console.log('Environment:', process.env.REACT_APP_ENV);

      const response = await axios.get(baseURLGetAIFieldLogic);
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      console.log('Response data type:', typeof response.data);
      console.log('Is response.data an array?', Array.isArray(response.data));

      // Handle different response formats
      let dataArray = [];
      if (Array.isArray(response.data)) {
        // Direct array response
        dataArray = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        // Wrapped in data property
        dataArray = response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        // Single object response, wrap in array
        dataArray = [response.data];
      } else {
        // Fallback to empty array
        dataArray = [];
      }

      console.log('Final data array:', dataArray);
      console.log('Final data array length:', dataArray.length);

      // Debug: Check Haribo record specifically
      const hariboRecord = dataArray.find(record => record.customer === 'Haribo');
      if (hariboRecord) {
        console.log('=== HARIBO RECORD FOUND ===');
        console.log('Haribo Products fields:', hariboRecord.fields?.Products);
        console.log('Shelf Life From Arrival in Haribo:', hariboRecord.fields?.Products?.['Shelf Life From Arrival']);
        console.log('All Products field names:', Object.keys(hariboRecord.fields?.Products || {}));
      } else {
        console.log('❌ No Haribo record found in response');
      }

      setRowData(dataArray);
    } catch (error) {
      console.error("=== AI FIELD LOGIC FETCH ERROR ===");
      console.error("Error details:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      console.error("URL attempted:", baseURLGetAIFieldLogic);

      // Set empty array on error to prevent AG Grid issues
      setRowData([]);
      toast.error(`Failed to fetch AI Field Logic data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setSelectedRecord(null);

    // Initialize with predefined static fields
    const initialFields = {
      Products: {
        'Brand Name': '',
        'Shelf Life From Arrival': ''
      },
      Marketing: {},
      Ingredients: {},
      "Allergens and Diet": {},
      Nutrition: {},
      "External Info": {},
      Hierarchy: {},
      Contacts: {}
    };

    setFormData({
      customer: '',
      fields: initialFields
    });
    setShowModal(true);
  };

  const handleView = (record) => {
    setModalMode('view');
    setSelectedRecord(record);

    // Ensure static fields are visible when viewing
    const existingFields = record.fields || {};
    const fieldsWithStatic = {
      Products: {
        'Brand Name': '',
        'Shelf Life From Arrival': '',
        ...existingFields.Products
      },
      Marketing: existingFields.Marketing || {},
      Ingredients: existingFields.Ingredients || {},
      "Allergens and Diet": existingFields["Allergens and Diet"] || {},
      Nutrition: existingFields.Nutrition || {},
      "External Info": existingFields["External Info"] || {},
      Hierarchy: existingFields.Hierarchy || {},
      Contacts: existingFields.Contacts || {}
    };

    setFormData({
      ...record,
      fields: fieldsWithStatic
    });
    setShowModal(true);
  };

  const handleEdit = (record) => {
    setModalMode('edit');
    setSelectedRecord(record);

    // Ensure static fields are present in existing records
    const existingFields = record.fields || {};
    const fieldsWithStatic = {
      Products: {
        'Brand Name': '',
        'Shelf Life From Arrival': '',
        ...existingFields.Products
      },
      Marketing: existingFields.Marketing || {},
      Ingredients: existingFields.Ingredients || {},
      "Allergens and Diet": existingFields["Allergens and Diet"] || {},
      Nutrition: existingFields.Nutrition || {},
      "External Info": existingFields["External Info"] || {},
      Hierarchy: existingFields.Hierarchy || {},
      Contacts: existingFields.Contacts || {}
    };

    setFormData({
      ...record,
      fields: fieldsWithStatic
    });
    setShowModal(true);
  };

  const handleDelete = (record) => {
    setRecordToDelete(record);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!recordToDelete) return;

    try {
      console.log('=== DELETING AI FIELD LOGIC ===');
      console.log('Record ID:', recordToDelete._id);
      console.log('Customer:', recordToDelete.customer);

      const response = await axios.delete(`${baseURLDeleteAIFieldLogic}/${recordToDelete._id}`);
      console.log('Delete response:', response.data);

      toast.success("AI Field Logic deleted successfully");
      setShowDeleteModal(false);
      setRecordToDelete(null);
      fetchAIFieldLogicData();
    } catch (error) {
      console.error("=== DELETE ERROR ===");
      console.error("Error details:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);

      const errorMessage = error.response?.data?.message || error.message || "Failed to delete AI Field Logic";
      toast.error(errorMessage);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setRecordToDelete(null);
  };

  const handleSave = async () => {
    try {
      console.log('=== SAVING AI FIELD LOGIC ===');
      console.log('Mode:', modalMode);
      console.log('Form data:', formData);

      if (modalMode === 'create') {
        console.log('Creating new record...');
        const response = await axios.post(baseURLCreateAIFieldLogic, formData);
        console.log('Create response:', response.data);
        toast.success("AI Field Logic created successfully");
      } else if (modalMode === 'edit') {
        console.log('Updating record ID:', selectedRecord._id);
        const response = await axios.put(`${baseURLUpdateAIFieldLogic}/${selectedRecord._id}`, formData);
        console.log('Update response:', response.data);
        toast.success("AI Field Logic updated successfully");
      }

      setShowModal(false);
      fetchAIFieldLogicData();
    } catch (error) {
      console.error("=== SAVE ERROR ===");
      console.error("Error details:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);

      const errorMessage = error.response?.data?.message || error.message || "Failed to save AI Field Logic";
      toast.error(errorMessage);
    }
  };

  const handleFieldChange = (section, fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      fields: {
        ...prev.fields,
        [section]: {
          ...prev.fields[section],
          [fieldName]: value
        }
      }
    }));
  };

  const addNewField = (section) => {
    setAddFieldSection(section);
    setNewFieldName('');
    setShowAddFieldModal(true);
  };

  const confirmAddField = () => {
    if (newFieldName.trim()) {
      handleFieldChange(addFieldSection, newFieldName.trim(), "");
      setShowAddFieldModal(false);
      setNewFieldName('');
      setAddFieldSection('');
    }
  };

  const cancelAddField = () => {
    setShowAddFieldModal(false);
    setNewFieldName('');
    setAddFieldSection('');
  };

  const removeField = (section, fieldName) => {
    if (window.confirm(`Remove field "${fieldName}"?`)) {
      setFormData(prev => {
        const newFields = { ...prev.fields[section] };
        delete newFields[fieldName];
        return {
          ...prev,
          fields: {
            ...prev.fields,
            [section]: newFields
          }
        };
      });
    }
  };

  // Define predefined static fields for each section
  const predefinedStaticFields = {
    Products: [
      { name: 'Brand Name', description: 'Brand name to populate Brand Name column in Excel output', placeholder: 'e.g., Haribo' },
      { name: 'Shelf Life From Arrival', description: 'Shelf life from arrival date', placeholder: 'e.g., 365 days' }
    ]
  };

  const renderFieldEditor = (section) => {
    const sectionFields = formData.fields[section] || {};
    const staticFields = predefinedStaticFields[section] || [];

    // Debug logging for Products section
    if (section === 'Products') {
      console.log('=== RENDERING PRODUCTS SECTION ===');
      console.log('Section fields:', sectionFields);
      console.log('Field names:', Object.keys(sectionFields));
      console.log('Shelf Life From Arrival present:', 'Shelf Life From Arrival' in sectionFields);
      console.log('Shelf Life From Arrival value:', sectionFields['Shelf Life From Arrival']);
      console.log('Brand Name present:', 'Brand Name' in sectionFields);
      console.log('Brand Name value:', sectionFields['Brand Name']);
    }

    // Combine static fields with existing fields, ensuring static fields are always present
    const allFields = { ...sectionFields };
    staticFields.forEach(staticField => {
      if (!(staticField.name in allFields)) {
        allFields[staticField.name] = '';
      }
    });

    return (
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6>{section} Fields ({Object.keys(allFields).length})</h6>
          {modalMode !== 'view' && (
            <Button size="sm" variant="outline-success" onClick={() => addNewField(section)}>
              <FaPlus /> Add Field
            </Button>
          )}
        </div>

        {/* Render static fields first */}
        {staticFields.map((staticField) => (
          <div key={staticField.name} className="mb-3">
            <div className="d-flex align-items-center gap-2">
              <Form.Label className="mb-0 flex-shrink-0" style={{ minWidth: '200px' }}>
                <span className="text-primary">⚙️</span> {staticField.name}:
              </Form.Label>
              <Form.Control
                type="text"
                value={allFields[staticField.name] || ''}
                onChange={(e) => handleFieldChange(section, staticField.name, e.target.value)}
                disabled={modalMode === 'view'}
                className="flex-grow-1"
                placeholder={staticField.placeholder}
                title={staticField.description}
              />
              <small className="text-muted" style={{ minWidth: '20px' }}>
                <span title={staticField.description}>ℹ️</span>
              </small>
            </div>
            <small className="text-muted ms-2">{staticField.description}</small>
          </div>
        ))}

        {/* Render custom fields */}
        {Object.entries(sectionFields).filter(([fieldName]) =>
          !staticFields.some(sf => sf.name === fieldName)
        ).map(([fieldName, fieldValue]) => (
          <div key={fieldName} className="mb-3">
            <div className="d-flex align-items-center gap-2">
              <Form.Label className="mb-0 flex-shrink-0" style={{ minWidth: '200px' }}>
                {fieldName}:
              </Form.Label>
              <Form.Control
                type="text"
                value={fieldValue || ''}
                onChange={(e) => handleFieldChange(section, fieldName, e.target.value)}
                disabled={modalMode === 'view'}
                className="flex-grow-1"
              />
              {modalMode !== 'view' && (
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => removeField(section, fieldName)}
                >
                  <FaTimes />
                </Button>
              )}
            </div>
          </div>
        ))}

        {Object.keys(allFields).length === 0 && (
          <Alert variant="info">No fields defined for this section</Alert>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="container-fluid px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="text-3xl font-bold">
              AI Field Logic Management
              <span className="text-lg font-normal text-gray-600 ml-3">
                ({rowData?.length || 0} {(rowData?.length || 0) === 1 ? 'record' : 'records'})
              </span>
            </h1>
            <Button variant="outline-primary" onClick={handleCreate}>
              <FaPlus className="me-2" />
              Create New Logic
            </Button>
          </div>

          <div className="ag-theme-material" style={{ height: "600px", width: "100%" }}>
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true
              }}
              pagination={true}
              paginationPageSize={20}
              loading={loading}
            />
          </div>
        </motion.div>
      </div>

      {/* Modal for Create/Edit/View */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            {modalMode === 'create' && 'Create AI Field Logic'}
            {modalMode === 'edit' && (
              <>
                Edit AI Field Logic
                {formData.customer && formData.customer.toLowerCase().includes('haribo') && (
                  <span
                    className="ms-2"
                    style={{
                      fontSize: '18px',
                      filter: 'hue-rotate(45deg) saturate(1.2)'
                    }}
                    title="Haribo Customer"
                  >
                    🍬
                  </span>
                )}
              </>
            )}
            {modalMode === 'view' && (
              <>
                View AI Field Logic
                {formData.customer && formData.customer.toLowerCase().includes('haribo') && (
                  <span
                    className="ms-2"
                    style={{
                      fontSize: '18px',
                      filter: 'hue-rotate(45deg) saturate(1.2)'
                    }}
                    title="Haribo Customer"
                  >
                    🍬
                  </span>
                )}
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="d-flex align-items-center">
                Customer
                {formData.customer && formData.customer.toLowerCase().includes('haribo') && (
                  <span
                    className="ms-2"
                    style={{
                      fontSize: '16px',
                      filter: 'hue-rotate(45deg) saturate(1.2)'
                    }}
                    title="Haribo Customer"
                  >
                    🍬
                  </span>
                )}
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.customer}
                onChange={(e) => setFormData(prev => ({ ...prev, customer: e.target.value }))}
                disabled={modalMode === 'view'}
                placeholder="Enter customer name"
              />
            </Form.Group>

            <Tabs defaultActiveKey="Products" className="mb-3">
              {fieldSections.map(section => (
                <Tab eventKey={section} title={section} key={section}>
                  {renderFieldEditor(section)}
                </Tab>
              ))}
            </Tabs>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
            <FaTimes className="me-2" />
            {modalMode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {modalMode !== 'view' && (
            <Button variant="outline-primary" onClick={handleSave}>
              <FaSave className="me-2" />
              {modalMode === 'create' ? 'Create' : 'Update'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={cancelDelete}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="text-danger d-flex align-items-center">
            <FaTrash className="me-2" />
            Confirm Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <div className="text-center py-3">
            <h5 className="mb-3">Are you sure you want to delete this AI Field Logic?</h5>
            {recordToDelete && (
              <div className="bg-light rounded p-3 mb-3">
                <strong>Customer:</strong> {recordToDelete.customer}
                <br />
                <small className="text-muted">
                  This action cannot be undone. All field configurations for this customer will be permanently removed.
                </small>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 justify-content-center">
          <Button
            variant="outline-secondary"
            onClick={cancelDelete}
            className="me-2"
          >
            <FaTimes className="me-2" />
            Cancel
          </Button>
          <Button
            variant="outline-danger"
            onClick={confirmDelete}
          >
            <FaTrash className="me-2" />
            Delete Permanently
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Field Modal */}
      <Modal
        show={showAddFieldModal}
        onHide={cancelAddField}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="text-success d-flex align-items-center">
            <FaPlus className="me-2" />
            Add New Field
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <div className="py-3">
            <div className="mb-3">
              <h6 className="mb-2">Section: <span className="text-primary">{addFieldSection}</span></h6>
              <p className="text-muted small mb-3">
                Add a new field to the {addFieldSection} section. This field will be available for configuration.
              </p>
            </div>

            <Form.Group>
              <Form.Label>Field Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter field name (e.g., 'Product Weight', 'Storage Temperature')"
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    confirmAddField();
                  }
                }}
                autoFocus
              />
              <Form.Text className="text-muted">
                Field names should be descriptive and unique within this section.
              </Form.Text>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 justify-content-end">
          <Button
            variant="outline-secondary"
            onClick={cancelAddField}
            className="me-2"
          >
            <FaTimes className="me-2" />
            Cancel
          </Button>
          <Button
            variant="outline-success"
            onClick={confirmAddField}
            disabled={!newFieldName.trim()}
          >
            <FaPlus className="me-2" />
            Add Field
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </>
  );
};

export default AIFieldLogicManager;
